/**
 * Positional line diff (LCS) and three-way merge used for collaborative
 * comment editing.
 *
 * All merging is index-based, so repeated identical lines never collapse
 * or duplicate, and repeated folds of the same remote state are no-ops.
 *
 * Conflict policy:
 * - Both sides inserted different lines at the same spot -> keep both
 *   (current first), since both are new content and must not be lost.
 * - Both sides rewrote the same base lines differently -> keep the local
 *   (current) version. The local user's save then propagates their copy,
 *   and the other client adopts it once it is clean (last writer wins,
 *   per line, without ever yanking text out from under an active editor).
 */

/**
 * LCS-based line diff.
 * @returns {Array<{bStart:number,bEnd:number,sStart:number,sEnd:number}>}
 *   Hunks replacing baseLines[bStart..bEnd) with sideLines[sStart..sEnd).
 */
export function diffLineHunks(baseLines, sideLines) {
  const n = baseLines.length;
  const m = sideLines.length;

  const dp = [];
  for (let i = 0; i <= n; i++) {
    dp.push(new Int32Array(m + 1));
  }
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (baseLines[i] === sideLines[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const hunks = [];
  let i = 0;
  let j = 0;
  let bStart = -1;
  let sStart = -1;

  const flush = (bEnd, sEnd) => {
    if (bStart !== -1) {
      hunks.push({ bStart, bEnd, sStart, sEnd });
      bStart = -1;
      sStart = -1;
    }
  };

  while (i < n && j < m) {
    if (baseLines[i] === sideLines[j]) {
      flush(i, j);
      i++;
      j++;
    } else {
      if (bStart === -1) {
        bStart = i;
        sStart = j;
      }
      if (dp[i + 1][j] >= dp[i][j + 1]) {
        i++;
      } else {
        j++;
      }
    }
  }

  if ((i < n || j < m) && bStart === -1) {
    bStart = i;
    sStart = j;
  }
  flush(n, m);

  return hunks;
}

/**
 * Three-way merge of lines, returning provenance refs so callers can reuse
 * existing objects (e.g. Draft.js ContentBlocks) for untouched lines.
 *
 * @returns {Array<{source:'current'|'remote', index:number}>}
 */
export function merge3LineRefs(baseLines, currentLines, remoteLines) {
  const curHunks = diffLineHunks(baseLines, currentLines);
  const remHunks = diffLineHunks(baseLines, remoteLines);

  const out = [];
  const pushRange = (source, from, to) => {
    for (let k = from; k < to; k++) {
      out.push({ source, index: k });
    }
  };

  let i = 0; // base line index
  let offC = 0; // currentIndex - baseIndex at stable boundaries
  let offR = 0;
  let ci = 0;
  let ri = 0;

  while (true) {
    const cH = curHunks[ci];
    const rH = remHunks[ri];

    if (!cH && !rH) {
      // Stable tail: identical in base, current, and remote.
      pushRange('current', i + offC, currentLines.length);
      break;
    }

    const nextStart = Math.min(
      cH ? cH.bStart : Infinity,
      rH ? rH.bStart : Infinity,
    );

    // Stable lines before the next change.
    pushRange('current', i + offC, nextStart + offC);
    i = nextStart;

    // Cluster overlapping hunks from both sides into one region.
    let end = i;
    const cCluster = [];
    const rCluster = [];
    let absorbed = true;
    while (absorbed) {
      absorbed = false;
      if (ci < curHunks.length) {
        const h = curHunks[ci];
        if (h.bStart < end || (h.bStart === end && end === i)) {
          cCluster.push(h);
          end = Math.max(end, h.bEnd);
          ci++;
          absorbed = true;
        }
      }
      if (ri < remHunks.length) {
        const h = remHunks[ri];
        if (h.bStart < end || (h.bStart === end && end === i)) {
          rCluster.push(h);
          end = Math.max(end, h.bEnd);
          ri++;
          absorbed = true;
        }
      }
    }

    const cStart = i + offC;
    for (const h of cCluster) {
      offC += (h.sEnd - h.sStart) - (h.bEnd - h.bStart);
    }
    const cEnd = end + offC;

    const rStart = i + offR;
    for (const h of rCluster) {
      offR += (h.sEnd - h.sStart) - (h.bEnd - h.bStart);
    }
    const rEnd = end + offR;

    if (rCluster.length === 0) {
      // Only the local user changed this region.
      pushRange('current', cStart, cEnd);
    } else if (cCluster.length === 0) {
      // Only the remote side changed this region.
      pushRange('remote', rStart, rEnd);
    } else {
      // A region whose base is empty (or only blank lines, e.g. brand new
      // notes) is additive on both sides — never drop either side.
      const baseSlice = baseLines.slice(i, end);
      resolveConflict({
        pushRange,
        currentLines,
        remoteLines,
        cStart,
        cEnd,
        rStart,
        rEnd,
        isPureInsert:
          end === i || baseSlice.every((line) => line === ''),
      });
    }

    i = end;
  }

  return out;
}

function resolveConflict({
  pushRange,
  currentLines,
  remoteLines,
  cStart,
  cEnd,
  rStart,
  rEnd,
  isPureInsert,
}) {
  const curSlice = currentLines.slice(cStart, cEnd);
  const remSlice = remoteLines.slice(rStart, rEnd);

  const identical =
    curSlice.length === remSlice.length &&
    curSlice.every((line, idx) => line === remSlice[idx]);
  if (identical) {
    pushRange('current', cStart, cEnd);
    return;
  }

  // Dedupe shared leading/trailing lines so subset additions merge cleanly
  // (e.g. current added [a], remote added [a, b] -> a, b).
  let prefix = 0;
  while (
    prefix < curSlice.length &&
    prefix < remSlice.length &&
    curSlice[prefix] === remSlice[prefix]
  ) {
    prefix++;
  }
  let suffix = 0;
  while (
    suffix < curSlice.length - prefix &&
    suffix < remSlice.length - prefix &&
    curSlice[curSlice.length - 1 - suffix] ===
      remSlice[remSlice.length - 1 - suffix]
  ) {
    suffix++;
  }

  pushRange('current', cStart, cStart + prefix);

  const curMidLen = curSlice.length - prefix - suffix;
  const remMidLen = remSlice.length - prefix - suffix;

  if (curMidLen === 0) {
    pushRange('remote', rStart + prefix, rEnd - suffix);
  } else if (remMidLen === 0) {
    pushRange('current', cStart + prefix, cEnd - suffix);
  } else if (isPureInsert) {
    // Both typed new lines at the same spot: keep both, local first.
    pushRange('current', cStart + prefix, cEnd - suffix);
    pushRange('remote', rStart + prefix, rEnd - suffix);
  } else {
    // Both rewrote the same lines: keep the local version (LWW on save).
    pushRange('current', cStart + prefix, cEnd - suffix);
  }

  pushRange('current', cEnd - suffix, cEnd);
}

/**
 * Convenience text-level wrapper (used by tests and non-editor callers).
 */
export function merge3Text(baseText, currentText, remoteText) {
  const baseLines = (baseText || '').split('\n');
  const currentLines = (currentText || '').split('\n');
  const remoteLines = (remoteText || '').split('\n');

  return merge3LineRefs(baseLines, currentLines, remoteLines)
    .map((ref) =>
      ref.source === 'current'
        ? currentLines[ref.index]
        : remoteLines[ref.index],
    )
    .join('\n');
}
